import { useContext, useEffect, useMemo } from 'react';

import { Badge } from '@capsule/consumer-design-system';

import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { utc } from 'moment';
import { useInView } from 'react-intersection-observer';

import { RefillSources, UNIFIED } from 'src/app/analytics/analyticsEvents';
import { Divider } from 'src/app/components/Elements/Divider';
import { Typography } from 'src/app/components/Elements/Typography';
import { CardItemOverflowMenu } from 'src/app/components/PageSections/CardItemOverflowMenuWrapper';
import { useOrderCount } from 'src/app/hooks/data/order';
import { useProcessRefill } from 'src/app/hooks/data/refill';
import { useCreateFirstAttemptRetrieval } from 'src/app/hooks/data/retrieval';
import { useLazySegmentTrack } from 'src/app/hooks/useLazySegmentTrack';
import { MedicationCardStateContext } from 'src/app/providers/MedicationCardStateProvider';
import { segmentTrack } from 'src/app/state/actions';
import {
  updateMedicationCardToBeInProcessRefillRequestedState,
  updateMedicationCardToBeInRefillRequestedState,
} from 'src/app/state/actions/homescreen';
import { getCurrentUserId } from 'src/app/state/selectors';
import { useDispatch, useSelector } from 'src/app/state/store';
import type {
  AnimationState,
  HomePrescriptionItem,
  MedicationMenuActionProps,
  ToggleMedicationCardSections,
  PrescriptionLifecycle,
} from 'src/app/types';
import { getFullName } from 'src/app/utils/patientUtils';
import { CTATypes, POPUP_MENU_LABELS, REFILL_STATUSES } from 'src/consts';
import { theme } from 'src/styles/theme';

import Button from '../../Elements/Button';
import { HorizontalTracker } from '../../Elements/Trackers/HorizontalTracker';

import { CheckoutSection } from './Sections/CheckoutSection';
import { DosageAndQuantitySection } from './Sections/DosageAndQuantitySection';
import { InstructionSection } from './Sections/InstructionSection';
import { MedicationNameSection } from './Sections/MedicationNameSection';
import { StatusTextSection } from './Sections/StatusTextSection';
import { SecondaryText } from './Sections/styles';
import {
  PrescriptionDates,
  PrescriptionDatesContainer,
  PrescriptionTrackerContainer,
  RefillRemainingCount,
} from './styles';
import styles from './styles.module.scss';
import { ctaAction, CTATypesToTextMap, getMenuActionForMedication } from './utils';

// This is from the backend for an expired drug for filtering
const STATUS_EXPIRED = 'prescription expired';

type CartMedication = {
  name: string;
  genericName?: string;
  stylizedPrice: string;
  dosage?: string;
  prescriptionFulfillmentId?: string;
  patientMedicationId: number;
  prescriptionId: string; // aka product ID
};

interface MedicationProps extends ToggleMedicationCardSections, MedicationMenuActionProps {
  isInCart: boolean;
  isReadyForCheckout: boolean;
  dispatchAddToCart(medication: CartMedication): void;
  dispatchRemoveFromCart(medication: CartMedication): void;
  animationState?: AnimationState;
  hasMultiplePatients: boolean;
  homeScreenItem: HomePrescriptionItem;
  goToMedDetailsOnMedNameClick: boolean;
  cart: object[];
  withLegacyAnimations: boolean;
  rootPrescriptionLifecycle?: PrescriptionLifecycle;
  showPrescriptionDates: boolean;
  showCta?: boolean;
}

const Medication = ({
  homeScreenItem,
  isInCart,
  isReadyForCheckout,
  isArchivedSection,
  dispatchArchive,
  hasMultiplePatients,
  // hasNoMedicationHistory,
  goToMedDetailsOnMedNameClick,
  showSecondaryName = true,
  showInstructions = false,
  showCheckout = true,
  showDosage = true,
  showRefillCount = true,
  showPatientName = false,
  showPrescriptionDates = false,
  showCta = true,
  showMenuActions,
  showAddToBag,
  dispatchAddToCart,
  dispatchRemoveFromCart,
  dispatchToggleArchiveSection,
  dispatchGetAllHomescreenItems,
  dispatchGoToMedicationDetails,
  setAnimationState,
  setOverflowHidden,
  animationState,
  cart,
  withLegacyAnimations,
  dispatchSetShowNewArchivedPageModal,
  dispatchSetMedicationLoadingState,
  rootPrescriptionLifecycle,
}: MedicationProps) => {
  const dispatch = useDispatch();
  const [trackAddToCart] = useLazySegmentTrack(UNIFIED.addToCart);
  const [trackRemoveFromCart] = useLazySegmentTrack(UNIFIED.removeFromCart);
  const [trackArchiveMedication] = useLazySegmentTrack(UNIFIED.archiveMedication);
  const [trackUnarchiveMedication] = useLazySegmentTrack(UNIFIED.unarchiveMedication);
  const [trackOpenMedicationAction] = useLazySegmentTrack(UNIFIED.openMedicationAction);
  const {
    id: prescriptionId,
    instructions,
    priceInDollars,
    refillsRemaining,
    refillStatusText,
    refillStatus,
    inBagRefillStatusText,
    statusText,
    dosage,
    quantity,
    insuranceApplied,
    name,
    genericName,
    patientMedicationId,
    prescriptionFulfillmentId,
    status,
    type,
    showPrice,
    cta,
    firstName,
    lastName,
    medicationId,
    expirationDate,
    prescribedDate,
    prescriptionLifecycle,
  } = homeScreenItem;
  const menuActions = getMenuActionForMedication({
    trackArchiveMedication,
    trackOpenMedicationAction,
    trackUnarchiveMedication,
    patientMedicationId,
    medicationId,
    refillStatusText,
    setOverflowHidden,
    dispatchArchive,
    dispatchToggleArchiveSection,
    dispatchGetAllHomescreenItems,
    isArchivedSection,
    setAnimationState,
    menuLabel: POPUP_MENU_LABELS.archive,
    dispatchGoToMedicationDetails,
    prescriptionId,
    dispatchSetShowNewArchivedPageModal,
    dispatchSetMedicationLoadingState,
    type,
  });

  const userId = useSelector(getCurrentUserId);

  const { mutateAsync: createFirstAttemptRetrieval } = useCreateFirstAttemptRetrieval();
  const { mutateAsync: processRefill } = useProcessRefill();
  const { medicationCardState, saveMedicationCardState } = useContext(MedicationCardStateContext);

  const stylizedPrice = priceInDollars >= 0 ? `$${priceInDollars.toFixed(2)}` : '';

  const medication: CartMedication = {
    name,
    genericName,
    stylizedPrice,
    dosage,
    prescriptionFulfillmentId,
    patientMedicationId,
    prescriptionId,
  };

  const analyticsPayload = {
    display_name_primary: name,
    price: stylizedPrice,
    dosage: dosage,
    prescription_fulfillment_id: prescriptionFulfillmentId,
    patientmedication_id: patientMedicationId,
    // we only show patient names when they have dependents
    has_dependents: hasMultiplePatients,
    OTC: false,
    quantity: 1,
    page: 'medications',
  };

  const { data: orderCount } = useOrderCount();
  const hasNoPriorOrders = useMemo(() => orderCount === 0, [orderCount]);

  const addToCart = () => {
    const addedItemToCart = window.localStorage.getItem('addedItemToCart');
    if (isEmpty(cart) && hasNoPriorOrders && !addedItemToCart) {
      dispatch(segmentTrack(UNIFIED.addToFirstRxCart));
      window.localStorage.setItem('addedItemToCart', 'true');
    }
    trackAddToCart({ ...analyticsPayload, position: null });
    dispatchAddToCart(medication);
  };

  const removeFromCart = () => {
    trackRemoveFromCart(analyticsPayload);
    dispatchRemoveFromCart(medication);
  };

  const { inView: isStatusTrackerInView, ref: statusTrackerRef } = useInView({ threshold: 0.25 });

  useEffect(() => {
    if (isStatusTrackerInView) {
      dispatch(
        segmentTrack(UNIFIED.viewStatusTracker, {
          patient_medications_id: patientMedicationId,
          state: refillStatusText,
        })
      );
    }
  }, [isStatusTrackerInView, dispatch, patientMedicationId, refillStatusText]);

  const shouldShowTransferringMedications = status !== 'pending' || type !== 'transfer';
  if (!shouldShowTransferringMedications) return null;

  const refillRemainingText = isInCart ? inBagRefillStatusText : refillStatusText;
  const isExpired = refillStatusText === REFILL_STATUSES.EXPIRED;
  const calculatedPatientName = getFullName({ firstName, lastName });

  const prescriptionLifecycleTotalSteps = prescriptionLifecycle?.maxPhaseCount;
  const prescriptionLifecycleCurrentPhase = prescriptionLifecycle?.currentPhase;
  const prescriptionLifecycleVariant = prescriptionLifecycle?.barState === 'active' ? 'active' : 'inactive';

  const refillStatusGreyed =
    isExpired || refillsRemaining === 0 || prescriptionLifecycle?.barState === 'inactive' ? styles.greyed : '';

  const shouldStatusTrackerShowCTA =
    !!rootPrescriptionLifecycle?.phases[rootPrescriptionLifecycle.currentPhase - 1].cta;

  const isShowingRefillRemainingCount = showRefillCount && !isArchivedSection;
  const isNewRefill = !!refillStatus?.showNewBadge;

  const createFirstAttemptRetrievalWithTimeout = () => {
    return setTimeout(async () => {
      saveMedicationCardState({ showRefillRequestedToastBanner: false });
      try {
        const response = await createFirstAttemptRetrieval({
          prescriptionId: parseInt(prescriptionId),
          userId: parseInt(userId),
        });
        dispatch(
          segmentTrack(UNIFIED.createReactiveRetrieval, {
            retrieval_ids: response?.retrievalId,
            task_ids: response?.taskId,
            automated: 'yes',
          })
        );
        dispatch(
          segmentTrack(UNIFIED.createManualRefillTask, {
            task_ids: response?.taskId,
            retrieval_ids: response?.retrievalId,
            automated: 'yes',
          })
        );
        await dispatchGetAllHomescreenItems(false);
      } catch (err) {
        console.log('err:', err);
      }
    }, 10000);
  };

  const processRefillWithTimeout = () => {
    return setTimeout(async () => {
      saveMedicationCardState({ showRefillRequestedToastBanner: false });
      try {
        const response = await processRefill({
          fillId: parseInt(prescriptionFulfillmentId), // TODO nullcheck?
        });
        dispatch(
          segmentTrack(UNIFIED.createManualRefillTask, {
            task_ids: response?.taskId,
            retrieval_ids: null,
            automated: 'yes',
          })
        );
        await dispatchGetAllHomescreenItems(false);
      } catch (err) {
        console.log('err:', err);
      }
    }, 10000);
  };

  const onRequestRefillClicked = async () => {
    dispatch(
      segmentTrack(UNIFIED.selectRequestRefillCta, {
        patientmedication_ids: patientMedicationId,
        source: RefillSources.requestRefillRxTab,
      })
    );
    dispatch(updateMedicationCardToBeInRefillRequestedState(prescriptionId));
    saveMedicationCardState({
      ...medicationCardState,
      showRefillRequestedToastBanner: true,
      prescriptionIdsInRequestRefillState: [...medicationCardState.prescriptionIdsInRequestRefillState, prescriptionId],
      patientMedicationIdInRequestRefillState: patientMedicationId,
      refillRequestTimeoutId: createFirstAttemptRetrievalWithTimeout(),
      source: RefillSources.requestRefillRxTab,
    });
  };

  const onProcessRefillClicked = async () => {
    dispatch(
      segmentTrack(UNIFIED.selectProcessRefillCta, {
        patientmedication_ids: patientMedicationId,
        source: RefillSources.processRefillRxTab,
      })
    );

    dispatch(updateMedicationCardToBeInProcessRefillRequestedState(prescriptionId));
    saveMedicationCardState({
      ...medicationCardState,
      showRefillRequestedToastBanner: true,
      prescriptionIdsInRequestRefillState: medicationCardState.prescriptionIdsInRequestRefillState,
      prescriptionIdsInProcessRefillState: [...medicationCardState.prescriptionIdsInProcessRefillState, prescriptionId],
      patientMedicationIdInRequestRefillState: medicationCardState.patientMedicationIdInRequestRefillState,
      patientMedicationIdInProcessRefillState: patientMedicationId,
      refillRequestTimeoutId: processRefillWithTimeout(),
      source: RefillSources.processRefillRxTab,
    });
  };

  return (
    <div
      className={classNames(
        styles.animationContainer,
        withLegacyAnimations && animationState?.collapse && styles.innerCardCollapse,
        withLegacyAnimations && animationState?.expand && styles.innerCardExpand
      )}
      id={`unarchived_${patientMedicationId}`}
    >
      <div className={styles.flexContainer}>
        {showPatientName && <Typography variant={'small'}>{calculatedPatientName}</Typography>}

        <MedicationNameSection
          primaryName={name}
          genericName={genericName}
          showSecondaryName={showSecondaryName}
          goToMedDetailsOnMedNameClick={goToMedDetailsOnMedNameClick}
          prescriptionId={prescriptionId}
          type={type}
        />

        {showDosage && <DosageAndQuantitySection dosage={dosage} quantity={quantity} />}

        {prescriptionLifecycle && (
          <PrescriptionTrackerContainer ref={statusTrackerRef}>
            <HorizontalTracker
              totalSteps={prescriptionLifecycleTotalSteps}
              currentStepIdx={prescriptionLifecycleCurrentPhase - 1}
              variant={prescriptionLifecycleVariant}
            />
          </PrescriptionTrackerContainer>
        )}

        {showInstructions && instructions && (
          <>
            <InstructionSection instructions={instructions} />
            <Divider backgroundColor="capsuleGreen00" />
          </>
        )}

        {isShowingRefillRemainingCount && (
          <RefillRemainingCount
            className={
              showPrice
                ? classNames(styles.refills, refillStatusGreyed, 'inspectlet-sensitive')
                : classNames(styles.hiddenPriceRefills, refillStatusGreyed, 'inspectlet-sensitive')
            }
          >
            {refillRemainingText}

            {isNewRefill && <Badge>New</Badge>}
          </RefillRemainingCount>
        )}

        {showMenuActions && (
          <div
            className={classNames(
              withLegacyAnimations && styles.fadeOut,
              withLegacyAnimations && animationState?.collapse && styles.startFadeOut
            )}
          >
            <CardItemOverflowMenu menuActions={menuActions} />
          </div>
        )}

        {showCheckout && isReadyForCheckout && (
          <CheckoutSection
            refillsRemaining={refillsRemaining}
            removeFromCart={removeFromCart}
            displayNamePrimary={name}
            addToCart={addToCart}
            insuranceApplied={insuranceApplied}
            price={stylizedPrice}
            isInCart={isInCart}
            showRefillCount={showRefillCount}
            showPrice={showPrice}
            showAddToBag={showAddToBag}
          />
        )}

        {showCheckout && !isReadyForCheckout && statusText && (
          <StatusTextSection
            statusText={statusText}
            prescriptionLifecycle={prescriptionLifecycle}
            refillStatus={refillStatus}
            shouldShowNewBadge={!isShowingRefillRemainingCount && isNewRefill}
          />
        )}

        {cta && cta.action.type === CTATypes.requestRefill && showCta && (
          <div>
            <Button style="secondary" size="small" onClick={onRequestRefillClicked}>
              {CTATypesToTextMap[cta.action.type]}
            </Button>
          </div>
        )}

        {cta && cta.action.type === CTATypes.processRefill && showCta && (
          <div>
            <Button style="secondary" size="small" onClick={onProcessRefillClicked}>
              {CTATypesToTextMap[cta.action.type]}
            </Button>
          </div>
        )}

        {cta &&
          cta.action.type !== CTATypes.requestRefill &&
          cta.action.type !== CTATypes.processRefill &&
          (!shouldStatusTrackerShowCTA || !prescriptionLifecycle) && (
            <div>
              <Button style="secondary" size="small" onClick={() => ctaAction(cta, dispatch)}>
                {CTATypesToTextMap[cta.action.type]}
              </Button>
            </div>
          )}

        {showPrescriptionDates && !prescriptionLifecycle && prescribedDate && expirationDate && (
          <>
            <Divider margin={theme.dividerMargins.lg} backgroundColor="capsuleGreen00" />

            <PrescriptionDatesContainer>
              <PrescriptionDates>
                <Typography variant="overline" color="capsuleGray50" marginBottom={'s3'}>
                  Prescribed
                </Typography>
                <Typography variant="body">{utc(prescribedDate).format('MM/DD/YYYY')}</Typography>
              </PrescriptionDates>
              <PrescriptionDates>
                <Typography variant="overline" color="capsuleGray50" marginBottom={'s3'}>
                  Prescription Expires
                </Typography>
                <Typography variant="body">{utc(expirationDate).format('MM/DD/YYYY')}</Typography>
              </PrescriptionDates>
            </PrescriptionDatesContainer>
          </>
        )}

        {status === STATUS_EXPIRED && isArchivedSection && <SecondaryText>Expired</SecondaryText>}
      </div>
    </div>
  );
};

export default Medication;
